import { RouteHelper } from 'classes/helpers/route.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { PseudoSection } from 'components/main/section-router/pseudo-section';
import { AdminPortal } from 'components/sections/admin-portal';
import { AnalyticsRouter } from 'components/sections/analytics/router';
import { FeatureDemo } from 'components/sections/feature-demo';
import { HitterLibrary } from 'components/sections/hitter-library';
import { MachineCalibration } from 'components/sections/machine-calibration';
import { PitchesRouter } from 'components/sections/pitches/router';
import { QuickSessionHoC } from 'components/sections/quick-session';
import { VideoLibraryHoC } from 'components/sections/video-library';
import { AuthContext } from 'contexts/auth.context';
import { CookiesContext } from 'contexts/cookies.context';
import { GameDataProvider } from 'contexts/game-data.context';
import { GlobalContext } from 'contexts/global.context';
import { HittersContext } from 'contexts/hitters.context';
import { SectionName } from 'enums/route.enums';
import { t } from 'i18next';
import { HOME, ISectionDef } from 'interfaces/i-section';
import { useContext, useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';

const RenderSection = (props: { def: ISectionDef }) => {
  const globalCx = useContext(GlobalContext);
  const cookiesCx = useContext(CookiesContext);
  const hittersCx = useContext(HittersContext);

  switch (props.def.section) {
    case SectionName.Pitches: {
      return <PitchesRouter />;
    }

    case SectionName.QuickSession: {
      return <QuickSessionHoC />;
    }

    case SectionName.HitterLibrary: {
      return (
        <HitterLibrary
          globalCx={globalCx}
          cookiesCx={cookiesCx}
          hittersCx={hittersCx}
        />
      );
    }

    case SectionName.VideoLibrary: {
      return <VideoLibraryHoC />;
    }

    case SectionName.Analytics: {
      return <AnalyticsRouter />;
    }

    case SectionName.AdminPortal: {
      return <AdminPortal />;
    }

    case SectionName.FeatureDemo: {
      return <FeatureDemo />;
    }

    case SectionName.CalibrateMachine: {
      return <MachineCalibration />;
    }

    case SectionName.Unavailable: {
      return (
        <PseudoSection
          header={t('main.unavailable')}
          content={
            <>
              <p>This section is currently unavailable.</p>
              <p>Please try again later.</p>
            </>
          }
        />
      );
    }

    case SectionName.GameInProgress: {
      return (
        <PseudoSection
          header={t('main.game-in-progress')}
          content={
            <>
              <p>There is a game in progress for your team.</p>
              <p>
                As a result, certain features of the application will be
                unavailable or restricted until after the game.
              </p>
            </>
          }
        />
      );
    }

    case SectionName.NotFound: {
      return (
        <PseudoSection
          header={t('main.not-found')}
          content={
            <p>
              Please check the URL or your access permissions and try again.
            </p>
          }
        />
      );
    }

    default: {
      return null;
    }
  }
};

export const SectionRouter = () => {
  const { current, featureFlags, checkTerms } = useContext(AuthContext);
  const { sections } = featureFlags;

  // check TOS 1x on mount
  useEffect(() => checkTerms(), []);

  return (
    <ErrorBoundary componentName="SectionRouter">
      <GameDataProvider>
        <Routes>
          <Route
            // PitchesRouter to handle subsection
            path={RouteHelper.getSlug([SectionName.Pitches], ['*'])}
            element={<RenderSection def={{ section: SectionName.Pitches }} />}
          />

          <Route
            path={RouteHelper.getSlug([SectionName.QuickSession])}
            element={
              current.quick_session ? (
                <RenderSection def={{ section: SectionName.QuickSession }} />
              ) : (
                <RenderSection def={{ section: SectionName.Unavailable }} />
              )
            }
          />

          <Route
            path={RouteHelper.getSlug([SectionName.HitterLibrary])}
            element={
              sections.hitterLibrary ? (
                <RenderSection def={{ section: SectionName.HitterLibrary }} />
              ) : (
                <RenderSection def={{ section: SectionName.Unavailable }} />
              )
            }
          />

          <Route
            path={RouteHelper.getSlug([SectionName.VideoLibrary])}
            element={
              sections.videoLibrary ? (
                <RenderSection def={{ section: SectionName.VideoLibrary }} />
              ) : (
                <RenderSection def={{ section: SectionName.Unavailable }} />
              )
            }
          />

          <Route
            // AnalyticsRouter to handle subsection
            path={RouteHelper.getSlug([SectionName.Analytics], ['*'])}
            element={
              sections.analytics ? (
                <RenderSection def={{ section: SectionName.Analytics }} />
              ) : (
                <RenderSection def={{ section: SectionName.Unavailable }} />
              )
            }
          />

          {
            <Route
              path={
                // AdminPortal to handle subsection
                RouteHelper.getSlug([SectionName.AdminPortal], ['*'])
              }
              element={
                sections.adminPortal ? (
                  <RenderSection def={{ section: SectionName.AdminPortal }} />
                ) : (
                  <RenderSection def={{ section: SectionName.Unavailable }} />
                )
              }
            />
          }

          <Route
            path={RouteHelper.getSlug([SectionName.CalibrateMachine])}
            element={
              <RenderSection def={{ section: SectionName.CalibrateMachine }} />
            }
          />

          <Route
            path={RouteHelper.getSlug([SectionName.FeatureDemo])}
            element={
              sections.featureDemo ? (
                <RenderSection def={{ section: SectionName.FeatureDemo }} />
              ) : (
                <RenderSection def={{ section: SectionName.Unavailable }} />
              )
            }
          />

          <Route
            path={RouteHelper.getSlug([SectionName.Unavailable])}
            element={
              <RenderSection def={{ section: SectionName.Unavailable }} />
            }
          />

          <Route
            path={RouteHelper.getSlug([SectionName.GameInProgress])}
            element={
              <RenderSection
                def={{
                  section: SectionName.GameInProgress,
                }}
              />
            }
          />

          <Route
            path={RouteHelper.getSlug([SectionName.NotFound])}
            element={<RenderSection def={{ section: SectionName.NotFound }} />}
          />

          <Route
            path="/*"
            element={<RenderSection def={{ section: HOME.section }} />}
          />
        </Routes>
      </GameDataProvider>
    </ErrorBoundary>
  );
};
